/**
 * API Client for Vectrax - Connects to Cloudflare Workers
 *
 * In development (localhost), uses Vite proxy to avoid CORS/bot protection issues.
 * In production, calls Workers directly.
 */

// Detect if running on localhost (dev mode with Vite proxy)
const isLocalDev = typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Worker URLs - use proxy paths in dev, direct URLs in production
const WORKERS = isLocalDev ? {
  stockData: '/api/stock-data',
  momentumSignals: '/api/momentum-signals',
  wavetrendSignals: '/api/wavetrend-signals',
  divergence: '/api/divergence',
  strategyDailyValues: '/api/strategy-daily-values',
  strategyTransactions: '/api/strategy-transactions',
  momentumScore: '/api/momentum-score',
  tickerHistory: '/api/ticker-history',
} : {
  stockData: 'https://vectrax-stock-data.thegpc806.workers.dev',
  momentumSignals: 'https://vectrax-momentum-signals.thegpc806.workers.dev',
  wavetrendSignals: 'https://vectrax-wavetrend-signals.thegpc806.workers.dev',
  divergence: 'https://vectrax-divergence.thegpc806.workers.dev',
  strategyDailyValues: 'https://vectrax-strategy-daily-values.thegpc806.workers.dev',
  strategyTransactions: 'https://vectrax-strategy-transactions.thegpc806.workers.dev',
  momentumScore: 'https://vectrax-momentum-score.thegpc806.workers.dev',
  tickerHistory: 'https://vectrax-ticker-history.thegpc806.workers.dev',
};

class APIClient {
  async fetchWatchlist(limit = 100, offset = 0, market = 'US') {
    const url = `${WORKERS.momentumSignals}?limit=${limit}&offset=${offset}&market=${market}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchChartData(ticker) {
    const url = `${WORKERS.momentumSignals}?chart=${ticker}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchHistory(ticker, period = '1y') {
    // Use ticker-history endpoint for OHLCV + indicators (LRO, WaveTrend, etc.)
    const url = `${WORKERS.tickerHistory}?ticker=${ticker}&period=${period}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchWaveTrend() {
    const url = WORKERS.wavetrendSignals;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchWaveTrendChart(ticker, market = 'US') {
    const url = `${WORKERS.wavetrendSignals}?chart=${ticker}&market=${market}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchStockData(type, params = {}) {
    const queryParams = new URLSearchParams({ type, ...params });
    const url = `${WORKERS.stockData}?${queryParams}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchRecommendations(market = 'US') {
    return this.fetchStockData('recommendations', { market });
  }

  async fetchFundamentals(market = 'US') {
    return this.fetchStockData('fundamentals', { market });
  }

  async fetchSignals(market = 'US') {
    return this.fetchStockData('signals', { market });
  }

  async fetchPeerGroups(market = 'US') {
    return this.fetchStockData('peers', { market });
  }

  async fetchDivergence(ticker, strategy = 'binary_critical') {
    const url = `${WORKERS.divergence}?ticker=${ticker}&strategy=${strategy}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchStrategyDailyValues(ticker, strategyType) {
    const params = new URLSearchParams({ ticker });
    if (strategyType) params.append('strategy_type', strategyType);
    const url = `${WORKERS.strategyDailyValues}?${params}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchStrategyTransactions(ticker, strategyType) {
    const params = new URLSearchParams({ ticker });
    if (strategyType) params.append('strategy_type', strategyType);
    const url = `${WORKERS.strategyTransactions}?${params}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async fetchMomentumScore(ticker) {
    const url = `${WORKERS.momentumScore}?ticker=${ticker}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }

  async checkVersion() {
    const url = `${WORKERS.momentumSignals}?version=check`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`API error: ${response.status}`);
    return response.json();
  }
}

export default new APIClient();
